// ============================================================
// API base for the new portals.
//
// In development this is the LOCAL dev API (which reads the emulator).
// In a PRODUCTION build it must be the live backend: a user's phone has no
// dev API, so falling back to localhost made the owner Property Management
// and rental/valuation views silently render empty. Same host the rest of the
// app already defaults to (see InvestorPortal.tsx).
//
// Override either with REACT_APP_PORTAL_API.
// ============================================================

const LIVE_API = 'https://investmenter-backend-1081814658115.europe-west1.run.app/api';
const DEV_API = 'http://localhost:4002/api/dev';

export const PORTAL_API =
  process.env.REACT_APP_PORTAL_API ||
  (process.env.NODE_ENV === 'production' ? LIVE_API : DEV_API);

// The logged-in user's token (owners authenticate through the normal app login,
// which stores this). Sent as a Bearer header so the production PM API
// (/api/pm/*, guarded by requireAnyAuth) accepts owner/EA requests. The local
// dev API ignores it, so this is harmless in development.
function authHeader(): Record<string, string> {
  try {
    const t = localStorage.getItem('authToken') || localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

// How long to wait before giving up on a request.
//
// The old default was a flat 4000ms with the rationale "a short timeout so the
// UI falls back to mock data quickly if the dev API isn't running". That is
// right in DEVELOPMENT and wrong in PRODUCTION: the live backend runs at
// min-instances=0 (docs/DEPLOY-BACKEND-PROMPT.md, rule 7), so a cold container
// takes ~28s to answer its first request and ~0.3s after that. Four seconds
// aborted every cold-start request in the portals — owner Property Management,
// the EA console, tenant login and OTP — and each caller then rendered its own
// version of "nothing here", which reads as a broken or empty account rather
// than a server that was still waking up.
//
// So: fail fast against a dev API that isn't running, and wait out the
// documented cold start in production. A warm production call still returns in
// well under a second — this ceiling only changes how long a genuinely cold or
// genuinely broken request waits before giving up.
const DEFAULT_TIMEOUT_MS = process.env.NODE_ENV === 'production' ? 45000 : 4000;

// Small fetch helper. Pass `timeoutMs` only when a call needs something other
// than the default above.
export async function portalFetch<T>(path: string, options?: RequestInit, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${PORTAL_API}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...authHeader(), ...(options?.headers || {}) },
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { const d: any = await res.json(); if (d && d.error) msg = d.error; } catch { /* no JSON body */ }
      throw new Error(msg);
    }
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}
